import axios from 'axios';

// Obtener la URL base de las variables de entorno
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60 segundos de timeout por defecto
  timeoutErrorMessage: 'La petición tardó demasiado tiempo. Por favor, intenta de nuevo.',
});

// Interceptor para añadir el token JWT a todas las peticiones
axiosClient.interceptors.request.use(
  (config) => {
    // Recupera el token del localStorage
    const token = localStorage.getItem('accessToken');
    
    // Si existe el token y el header de autorización no está presente, añádelo
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Necesario para peticiones multipart/form-data (subida de archivos), 
    // pero generalmente se omite el Content-Type para que Axios lo configure automáticamente con FormData.
    if (config.data instanceof FormData) {
         delete config.headers['Content-Type'];
    }

    try {
      const activeProfile = localStorage.getItem('activeConfigProfile');
      if (activeProfile) {
        config.headers['X-Config-Profile'] = activeProfile;
      }
    } catch {}
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores y reintentos
axiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Manejar errores 401 (No autorizado) y redirigir al login
    if (error.response && error.response.status === 401) {
      // Registrar en el log y forzar logout + redirección al login
      try {
        console.warn('Token expirado o inválido. Cerrando sesión y redirigiendo a /login.', error.response?.data);
      } catch {}
      try {
        // Guardar motivo para mostrar aviso en la pantalla de login
        localStorage.setItem('logoutReason', 'expired_token');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
      } catch {}
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // Manejar errores de conexión/timeout con reintentos
    const config = error.config;
    if (!config || !config.__retryCount) {
      config.__retryCount = 0;
    }

    // Solo reintentar para errores de red o timeout, no para errores HTTP (4xx, 5xx con respuesta)
    const shouldRetry = 
      (!error.response || error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' || error.message === 'Network Error') &&
      config.__retryCount < 3 && // Máximo 3 reintentos
      !config.__isRetryRequest; // Evitar loops infinitos

    if (shouldRetry) {
      config.__retryCount += 1;
      config.__isRetryRequest = true;
      
      // Esperar antes de reintentar (exponential backoff)
      const delay = Math.min(1000 * Math.pow(2, config.__retryCount - 1), 10000);
      console.log(`Reintentando petición (intento ${config.__retryCount}/3) después de ${delay}ms...`);
      
      await new Promise(resolve => setTimeout(resolve, delay));
      
      return axiosClient(config);
    }

    // Si es un error de timeout, mostrar mensaje más claro
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      console.error('Timeout en la petición:', error.config?.url);
      error.message = 'La petición tardó demasiado tiempo. Por favor, verifica tu conexión y vuelve a intentarlo.';
    }

    return Promise.reject(error);
  }
);

export default axiosClient;